var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { DefaultLoadingManager, DoubleSide, ImageLoader, Mesh, PlaneGeometry, Texture, UniformsUtils, RGBAFormat } from "three";
import { floorPowerOfTwo, isPowerOfTwo } from "flavr-common";
import { Euler, Quaternion, Vector2, Vector3 } from "flavr-math";
import { IMAGE_SHADER } from "./image.shader";
import { globalCameraWorld } from "../shader/GlobalUniforms";
import { Renderable } from "../renderable/Renderable";
import { CustomShaderMaterial } from "../shader/CustomShaderMaterial";
import { peerFileSystemService } from "../../multiplayer/peer";
import { offscreenService } from "../../offscreen";
import { modelPool } from "@modules/model";
const tmpVector31 = new Vector3();
const tmpVector32 = new Vector3();
const tmpQuaternion1 = new Quaternion();
const tmpEuler1 = new Euler();
const IMAGE_NOT_FOUND_FILE = "img/FlaVR_Final_Blue.png";
const LOADING_TEXTURE_FILE = "img/Loading.jpg";
const planeGeometry = new PlaneGeometry(1, 1, 1, 1);
const loadedTextures = {};
const imageLoader = new ImageLoader(DefaultLoadingManager);
imageLoader.setCrossOrigin("anonymous");
const texureNotFound = new Texture();
imageLoader.load(IMAGE_NOT_FOUND_FILE, (image) => {
    texureNotFound.image = image;
    texureNotFound.format = RGBAFormat;
    texureNotFound.needsUpdate = true;
});
const loadingMaterial = new CustomShaderMaterial({
    defines: { USE_VIEW_DIST_DISCARD: "", USE_FLIP_TEXTURE: "" },
    uniforms: UniformsUtils.clone(IMAGE_SHADER.uniforms),
    vertexShader: IMAGE_SHADER.vertexShader,
    fragmentShader: IMAGE_SHADER.fragmentShader,
    lights: false,
    transparent: true
});
loadingMaterial.uniforms.cameraWorld.value = globalCameraWorld;
const loadingTexture = new Texture();
imageLoader.load(LOADING_TEXTURE_FILE, (image) => {
    loadingTexture.image = image;
    loadingTexture.format = RGBAFormat;
    loadingTexture.needsUpdate = true;
    loadingMaterial.uniforms.map.value = loadingTexture;
    loadingMaterial.needsUpdate = true;
});
export function takeTexture(textureHash) {
    const textureInfo = loadedTextures[textureHash];
    if (textureInfo && textureInfo.countUsed >= 0) {
        ++textureInfo.countUsed;
    }
    return textureInfo;
}
const pendingTextures = {};
export function loadTexture(url, textureHash, entity, facet) {
    return __awaiter(this, void 0, void 0, function* () {
        let pendings = pendingTextures[textureHash];
        if (!pendings) {
            pendings = pendingTextures[textureHash] = { loaded: false, pendings: [] };
            pendings.pendings.push({ entity, facet });
            const fileId = peerFileSystemService.checkShareUri(url);
            if (fileId !== undefined) {
                try {
                    const peerFile = peerFileSystemService.subscribePeerRemoteFile(fileId);
                    yield peerFile.onLoadedPromise();
                    peerFile.onUnloaded.add(() => {
                        for (const it of pendings.pendings) {
                            if (!it.entity.destroyed) {
                                loadedTextures[textureHash] = {
                                    texture: texureNotFound,
                                    scale: new Vector2(1, 1),
                                    countUsed: 0
                                };
                                it.entity.changeTexture(texureNotFound, 1, 1);
                            }
                        }
                        delete pendingTextures[textureHash];
                    });
                    const u = yield peerFile.getOrCreateUrl();
                    if (u === undefined)
                        return;
                    url = u;
                }
                catch (_a) {
                    // console.error(error);
                    for (const it of pendings.pendings) {
                        if (!it.entity.destroyed) {
                            loadedTextures[textureHash] = {
                                texture: texureNotFound,
                                scale: new Vector2(1, 1),
                                countUsed: 0
                            };
                            it.entity.changeTexture(texureNotFound, 1, 1);
                        }
                    }
                    delete pendingTextures[textureHash];
                    return;
                }
            }
            const offscreenId = offscreenService.checkUri(url);
            if (offscreenId !== undefined) {
                let texture = offscreenService.getCanvasTexture(offscreenId);
                if (!texture) {
                    texture = texureNotFound;
                }
                for (const it of pendings.pendings) {
                    if (!it.entity.destroyed) {
                        loadedTextures[textureHash] = {
                            texture: texureNotFound,
                            scale: new Vector2(1, 1),
                            countUsed: 0
                        };
                        it.entity.changeTexture(texture, 1, 1);
                    }
                }
                delete pendingTextures[textureHash];
                return;
            }
            imageLoader.load(url, (image) => {
                pendings.loaded = true;
                const texture = new Texture();
                let textureCreated = false;
                let scaleX = 1;
                let scaleY = 1;
                let width = image.naturalWidth;
                let height = image.naturalHeight;
                let loadedTexture;
                for (const it of pendings.pendings) {
                    if (it.entity.destroyed) {
                        console.error("texture destroywed");
                        continue;
                    }
                    if (!textureCreated) {
                        textureCreated = true;
                        if (it.facet.param) {
                            if (it.facet.param.width) {
                                width = it.facet.param.width;
                            }
                            else if (it.facet.param.maxWidth && width > it.facet.param.maxWidth) {
                                width = it.facet.param.maxWidth;
                            }
                            if (it.facet.param.height) {
                                height = it.facet.param.height;
                            }
                            else if (it.facet.param.maxHeight && height > it.facet.param.maxHeight) {
                                height = it.facet.param.maxHeight;
                            }
                        }
                        // The Texture dimensions must be power of two
                        if ((it.facet.param && (it.facet.param.width || it.facet.param.height || it.facet.param.maxWidth || it.facet.param.maxHeight)) ||
                            !isPowerOfTwo(width) ||
                            !isPowerOfTwo(height)) {
                            const canvas = document.createElement("canvas");
                            const context = canvas.getContext("2d");
                            if (context) {
                                canvas.width = width = floorPowerOfTwo(width);
                                canvas.height = height = floorPowerOfTwo(height);
                                context.drawImage(image, 0, 0, canvas.width, canvas.height);
                            }
                            texture.image = canvas;
                        }
                        else {
                            texture.image = image;
                        }
                        texture.format = RGBAFormat;
                        texture.needsUpdate = true;
                        const aspect = width / height;
                        if (width > height) {
                            scaleY = 1 / aspect;
                        }
                        else {
                            scaleX = aspect;
                        }
                        loadedTexture = loadedTextures[textureHash] = {
                            texture,
                            scale: new Vector2(scaleX, scaleY),
                            countUsed: 0
                        };
                    }
                    it.entity.changeTexture(texture, scaleX, scaleY);
                    if (loadedTexture) {
                        ++loadedTexture.countUsed;
                    }
                }
                delete pendingTextures[textureHash];
            }, undefined, () => {
                let textureCreated = false;
                for (const it of pendings.pendings) {
                    if (!it.entity.destroyed) {
                        if (!textureCreated) {
                            textureCreated = true;
                            loadedTextures[textureHash] = {
                                texture: texureNotFound,
                                scale: new Vector2(1, 1),
                                countUsed: 0
                            };
                        }
                        it.entity.changeTexture(texureNotFound, 1, 1);
                    }
                }
                delete pendingTextures[textureHash];
            });
        }
        else {
            pendings.pendings.push({ entity, facet });
        }
    });
}
function giveBackTexture(textureHash) {
    const textureInfo = loadedTextures[textureHash];
    if (textureInfo && textureInfo.countUsed > 0 && textureInfo.texture !== texureNotFound) {
        --textureInfo.countUsed;
        if (textureInfo.countUsed === 0) {
            textureInfo.texture.dispose();
            delete loadedTextures[textureHash];
        }
    }
}
let unique = 0;
export class ImageRenderable extends Renderable {
    constructor(owner) {
        super(owner);
        this.type = ImageRenderable.typestatic;
        this.destroyed = false;
        this.textureHash = "";
        this.material = new CustomShaderMaterial({
            defines: {
                USE_VIEW_DIST_DISCARD: "",
                USE_FLIP_TEXTURE: "",
                CUSTOM_PROGRAM_CACHE_KEY: `${unique++}`
            },
            uniforms: UniformsUtils.clone(IMAGE_SHADER.uniforms),
            vertexShader: IMAGE_SHADER.vertexShader,
            fragmentShader: IMAGE_SHADER.fragmentShader,
            lights: false,
            transparent: true,
            side: DoubleSide
        });
        this.material.uniforms.cameraWorld.value = globalCameraWorld;
        const parentRoom = modelPool.getModelOrThrow(owner.modelUID).getRoomOrThrow();
        this.material.uniforms.roomMatrix.value = owner.renderer.getRoomTransformMatrix(parentRoom.getUID());
        this.mesh = this.createOrUpdateMesh(this.material);
    }
    destroy() {
        this.destroyed = true;
        if (this.mesh) {
            this.mesh.parent && this.mesh.parent.remove(this.mesh);
        }
        this.destroyTexture();
        if (this.material) {
            this.material.dispose();
        }
    }
    destroyTexture() {
        if (this.textureHash) {
            giveBackTexture(this.textureHash);
            this.textureHash = "";
        }
    }
    setVisible(visible) {
        if (this.mesh)
            this.mesh.visible = visible;
    }
    changeTexture(texture, scaleX, scaleY) {
        this.material.uniforms.map.value = texture;
        this.material.uniforms.imageScale.value.set(scaleX, scaleY);
        if (this.mesh) {
            this.mesh.material = this.material;
        }
    }
    generateTextureHash(facet) {
        this.textureHash = facet.url + "-";
        if (facet.param && (facet.param.height || facet.param.width || facet.param.maxHeight || facet.param.maxWidth)) {
            if (facet.param.height)
                this.textureHash += `${floorPowerOfTwo(facet.param.height)}`;
            this.textureHash += "-";
            if (facet.param.width)
                this.textureHash += `${floorPowerOfTwo(facet.param.width)}`;
            this.textureHash += "-";
            if (facet.param.maxHeight)
                this.textureHash += `${floorPowerOfTwo(facet.param.maxHeight)}`;
            this.textureHash += "-";
            if (facet.param.maxWidth)
                this.textureHash += `${floorPowerOfTwo(facet.param.maxWidth)}`;
            this.textureHash += "-";
        }
    }
    updateFacet(facet) {
        var _a, _b, _c;
        this.destroyTexture();
        this.generateTextureHash(facet);
        let color = 0xffffff;
        if (facet.param) {
            this.material.uniforms.snap.value.set((_a = facet.param.snapPitch) !== null && _a !== void 0 ? _a : Math.PI * 2, (_b = facet.param.snapYaw) !== null && _b !== void 0 ? _b : Math.PI * 2);
            this.material.uniforms.depthOffset.value = (_c = facet.param.depthOffset) !== null && _c !== void 0 ? _c : 0;
            if (facet.param.color !== undefined) {
                color = facet.param.color;
            }
        }
        else {
            this.material.uniforms.snap.value.set(Math.PI * 2, Math.PI * 2);
            this.material.uniforms.depthOffset.value = 0;
        }
        ;
        this.material.uniforms.diffuse.value.setHex(color);
        const textureInfo = takeTexture(this.textureHash);
        if (textureInfo) {
            this.material.uniforms.map.value = textureInfo.texture;
            this.material.uniforms.imageScale.value.copy(textureInfo.scale);
            this.createOrUpdateMesh(this.material);
        }
        else {
            this.material.uniforms.map.value = loadingTexture;
            this.createOrUpdateMesh(this.material);
            void loadTexture(facet.url, this.textureHash, this, facet);
        }
        if (this.mesh && !this.mesh.parent) {
            this.owner.renderer.addToScene(this.mesh, this.owner.modelUID);
        }
    }
    createOrUpdateMesh(material) {
        if (this.mesh) {
            this.mesh.material = material;
        }
        else {
            this.mesh = new Mesh(planeGeometry, material);
            this.mesh.matrixAutoUpdate = false;
            this.mesh.matrixWorldNeedsUpdate = false;
        }
        return this.mesh;
    }
    updateTransform() {
        if (!this.mesh)
            return;
        this.owner.getWorldTransform(tmpVector31, tmpQuaternion1, tmpVector32);
        tmpEuler1.setFromQuaternion(tmpQuaternion1, "YZX");
        const mat = this.mesh.material;
        mat.uniforms.modelPosition.value.copy(tmpVector31);
        mat.uniforms.modelRotation.value.set(tmpEuler1.x, tmpEuler1.y, tmpEuler1.z);
        mat.uniforms.modelScale.value.copy(tmpVector32);
        this.mesh.matrix.copy(this.owner.matrixWorld);
        this.mesh.matrixWorldNeedsUpdate = true;
    }
}
ImageRenderable.typestatic = "Image";
